/**
 * EventCoveragePeriod - Event dates with auto-detection
 * 
 * This component is used AFTER financial data upload (Step 3)
 * to allow auto-detection of the loss period from the uploaded data.
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Search, Sparkles, TrendingDown, Calendar } from 'lucide-react';
import axios from 'axios';
import { getDetectionPayload, hasEnoughDataForDetection } from '../../utils/financialDataNormalizer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface DetectedLossPeriod {
  coverage_year: number;
  loss_months: number[];
  suggested_event_start: string;
  suggested_event_end: string;
  confidence: 'high' | 'medium' | 'low';
  decline_percentage: number;
  baseline_years: number[];
  analysis_notes: string[];
}

interface EventCoveragePeriodProps {
  financialData: any;
  plFiles?: any[];  // P&L files from upload
  eventStartDate: string;
  eventEndDate: string;
  femaDeclarationNumber: string;
  onDateChange: (field: 'event_start_date' | 'event_end_date' | 'fema_declaration_number', value: string) => void;
  businessAddress?: string;
}

export default function EventCoveragePeriod({
  financialData,
  plFiles,
  eventStartDate,
  eventEndDate,
  femaDeclarationNumber,
  onDateChange,
  businessAddress
}: EventCoveragePeriodProps) {
  const [detectedLossPeriod, setDetectedLossPeriod] = useState<DetectedLossPeriod | null>(null);
  const [detectingLossPeriod, setDetectingLossPeriod] = useState(false);
  const [detectionComplete, setDetectionComplete] = useState(false);
  
  // FEMA state
  const [femaSuggestions, setFemaSuggestions] = useState<any[]>([]);
  const [loadingFema, setLoadingFema] = useState(false);
  const [femaError, setFemaError] = useState('');
  const [showFemaSuggestions, setShowFemaSuggestions] = useState(false);

  // Auto-detect loss period when financial data is available
  // Uses the unified normalizer to handle ALL data formats consistently
  useEffect(() => {
    const detectLossPeriod = async () => {
      if (detectionComplete) return;
      
      // Use the normalizer to check if we have enough data
      if (!hasEnoughDataForDetection(financialData, plFiles)) {
        console.log('⚠️ Not enough year data for auto-detection (need 2+ years)');
        setDetectionComplete(true);
        return;
      }
      
      // Get the detection payload in the correct format (handles ALL formats)
      const requestPayload = getDetectionPayload(financialData, plFiles);
      if (!requestPayload) {
        console.log('⚠️ Could not build detection payload');
        setDetectionComplete(true);
        return;
      }
      
      setDetectingLossPeriod(true);
      try {
        console.log('🔍 Calling loss period detection API...');
        const response = await axios.post(`${BACKEND_URL}/api/v1/reports/detect-loss-period`, requestPayload);
        
        if (response.data.success && response.data.detection) {
          const detection = response.data.detection;
          setDetectedLossPeriod(detection);
          console.log('✅ AUTO-DETECTED loss period:', detection);
          console.log('📅 Suggested dates:', detection.suggested_event_start, 'to', detection.suggested_event_end);
          console.log('📈 Decline:', detection.decline_percentage + '%', 'Confidence:', detection.confidence);
          
          // AUTO-APPLY dates immediately!
          console.log('🎯 AUTO-APPLYING detected dates...');
          onDateChange('event_start_date', detection.suggested_event_start);
          onDateChange('event_end_date', detection.suggested_event_end);
        }
      } catch (error) {
        console.error('❌ Loss period detection failed:', error);
      } finally {
        setDetectingLossPeriod(false);
        setDetectionComplete(true);
      }
    };
    
    detectLossPeriod();
  }, [financialData, plFiles, detectionComplete, onDateChange]);

  // Apply detected dates manually
  const applyDetectedDates = () => {
    if (detectedLossPeriod) {
      onDateChange('event_start_date', detectedLossPeriod.suggested_event_start);
      onDateChange('event_end_date', detectedLossPeriod.suggested_event_end);
    }
  };

  // Fetch FEMA suggestions
  const fetchFemaSuggestions = async () => {
    if (!eventStartDate || !eventEndDate) {
      setFemaError('Please set event dates first to search for FEMA declarations');
      return;
    }

    setLoadingFema(true);
    setFemaError('');
    
    try {
      // Parse address for state
      let state = '';
      if (businessAddress) {
        const stateMatch = businessAddress.match(/,\s*([A-Z]{2})\s+\d{5}/);
        if (stateMatch) {
          state = stateMatch[1];
        }
      }

      const response = await axios.get(`${BACKEND_URL}/api/v1/fema/search`, {
        params: {
          start_date: eventStartDate,
          end_date: eventEndDate,
          state: state || undefined
        }
      });

      if (response.data.declarations && response.data.declarations.length > 0) {
        setFemaSuggestions(response.data.declarations);
        setShowFemaSuggestions(true);
      } else {
        setFemaError('No FEMA declarations found for this date range and location');
      }
    } catch (error) {
      console.error('FEMA search error:', error);
      setFemaError('Failed to search FEMA declarations');
    } finally {
      setLoadingFema(false);
    }
  };

  return (
    <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50 mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-6 h-6 text-blue-600" />
        <div>
          <h3 className="text-lg font-bold text-blue-900">Event Coverage Period</h3>
          <p className="text-sm text-blue-700">When did the business interruption occur?</p>
        </div>
      </div>
      
      {/* Auto-Detection Status */}
      {detectingLossPeriod && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-700">Analyzing financial data to detect loss period...</span>
          </div>
        </div>
      )}
      
      {/* Auto-Detected Loss Period Banner */}
      {detectedLossPeriod && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Sparkles className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-green-800">🔍 Loss Period Auto-Detected</h4>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  detectedLossPeriod.confidence === 'high' ? 'bg-green-200 text-green-800' :
                  detectedLossPeriod.confidence === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                  'bg-gray-200 text-gray-800'
                }`}>
                  {detectedLossPeriod.confidence} confidence
                </span>
              </div>
              <p className="text-sm text-green-700 mt-1">
                Based on your financial data ({detectedLossPeriod.baseline_years.join(', ')} → {detectedLossPeriod.coverage_year}), 
                we detected a <strong>{detectedLossPeriod.decline_percentage}% decline</strong> in {detectedLossPeriod.loss_months.length} months.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-gray-700">
                  Suggested: {detectedLossPeriod.suggested_event_start} to {detectedLossPeriod.suggested_event_end}
                </span>
              </div>
              {(eventStartDate !== detectedLossPeriod.suggested_event_start || 
                eventEndDate !== detectedLossPeriod.suggested_event_end) && (
                <button
                  type="button"
                  onClick={applyDetectedDates}
                  className="mt-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Apply Suggested Dates
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Date Inputs */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Event Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={eventStartDate}
            onChange={(e) => onDateChange('event_start_date', e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent font-medium"
            required
          />
          <p className="text-xs text-gray-600 mt-1">When disruption began</p>
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Event End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={eventEndDate}
            onChange={(e) => onDateChange('event_end_date', e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent font-medium"
            required
          />
          <p className="text-xs text-gray-600 mt-1">When operations normalized</p>
        </div>
      </div>
      
      {/* FEMA Declaration Number */}
      <div className="mt-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          FEMA Declaration Number <span className="text-gray-500 font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          value={femaDeclarationNumber}
          onChange={(e) => onDateChange('fema_declaration_number', e.target.value)}
          placeholder="e.g., DR-4393-NC or leave blank if not applicable"
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-gray-600">
            FEMA disaster declaration number (strengthens claim validation)
          </p>
          {eventStartDate && eventEndDate && !loadingFema && (
            <button
              type="button"
              onClick={fetchFemaSuggestions}
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
            >
              <Search className="w-3 h-3" />
              Search FEMA
            </button>
          )}
        </div>

        {/* FEMA Loading */}
        {loadingFema && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-700">Searching FEMA declarations...</span>
          </div>
        )}

        {/* FEMA Error */}
        {femaError && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-700">{femaError}</span>
          </div>
        )}

        {/* FEMA Suggestions */}
        {femaSuggestions.length > 0 && showFemaSuggestions && (
          <div className="mt-3 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <h4 className="font-bold text-green-900">
                  Found {femaSuggestions.length} Relevant FEMA Declaration{femaSuggestions.length > 1 ? 's' : ''}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => setShowFemaSuggestions(false)}
                className="text-xs text-green-700 hover:text-green-900"
              >
                Hide
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {femaSuggestions.slice(0, 5).map((suggestion: any, idx: number) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                    femaDeclarationNumber === suggestion.disaster_number
                      ? 'bg-green-100 border-green-500'
                      : 'bg-white border-green-200 hover:border-green-400'
                  }`}
                  onClick={() => {
                    onDateChange('fema_declaration_number', suggestion.disaster_number);
                    setShowFemaSuggestions(false);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-green-900">{suggestion.disaster_number}</span>
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded">
                          {suggestion.match_score || suggestion.relevance_score}% match
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{suggestion.title || suggestion.declaration_title}</p>
                    </div>
                    {femaDeclarationNumber === suggestion.disaster_number && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
